import React, { createContext, useContext, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useAuth } from './AuthContext'
import { prepareHeaders } from '../Helpers/prepareHeaders'

const APIContext = createContext()

export function useAPI() {
  return useContext(APIContext)
}

export function APIProvider({ children }) {
  const { logout } = useAuth()
  const baseURL = process.env.REACT_APP_BACKEND_BASE_URL
  const GET = 'get'
  const POST = 'post'
  const PUT = 'put'
  // fetch() only case-normalizes GET/POST/PUT/DELETE/HEAD/OPTIONS; PATCH is sent
  // verbatim, so it must be uppercase or CORS preflight mismatches the server.
  const PATCH = 'PATCH'
  const DELETE = 'delete'

  const INCLUDE = 'include'
  const OMIT = 'omit'

  // function prepareHeaders() {
  //   let headers = new Headers()

  //   headers.append('Content-Type', 'application/json')
  //   headers.append('Accept', 'application/json')

  //   headers.append('X-MMUF-API-Version', '1.0.0')
  //   headers.append('X-MMUF-Client', 'MMUF/1.0.0')

  //   return headers
  // }

  function fetchData(
    url,
    method,
    headers,
    body,
    credentials,
    success,
    failure
  ) {
    const newController = new AbortController()
    const signal = newController.signal

    fetch(url, {
      method: method,
      headers: headers,
      body: body,
      signal,
      credentials: credentials,
    })
      .then(async data => {
        if (data.status === 200) {
          success(data)
        } else if (data.status === 401) {
          logout()
        } else {
          // Try to get detailed error message from response body
          try {
            const errorText = await data.text();
            if (errorText) {
              console.error('Server error response:', errorText);
              try {
                const errorJson = JSON.parse(errorText);
                
                // For 409 conflicts, pass structured error data
                if (data.status === 409) {
                  console.log('=== 409 RESPONSE DEBUG ===');
                  console.log('Response status:', data.status);
                  console.log('Response headers:', [...data.headers.entries()]);
                  console.log('Parsed error JSON:', errorJson);
                  console.log('Available keys in error:', Object.keys(errorJson));
                  console.log('=== END 409 DEBUG ===');
                  
                  const errorObj = {
                    status: 409,
                    message: errorJson.message || errorJson.error || 'Conflict',
                    data: errorJson,
                    originalResponse: data
                  };
                  failure(errorObj);
                } else if (data.status === 400) {
                  // Handle 400 validation errors - use backend-formatted message if available
                  let userMessage = errorJson.message || 'Please fix the following issues:';
                  
                  if (errorJson.validation_errors) {
                    const validationErrors = errorJson.validation_errors;

                    if (!errorJson.message) {
                      // Check if this is a timing validation error and create detailed message
                      const isTimingError = validationErrors.some(error =>
                        error.lyrics_sync && error.lyrics_sync.includes('valid start/end')
                      );

                      if (isTimingError) {
                        userMessage = 'Invalid timing detected: One or more lyrics have start times that occur after their end times. Please check your timing values in the lyrics editor and ensure all start times come before their corresponding end times. Look for lines where the start time (left input) is greater than the end time (right input).';
                      } else {
                        // For all other validation errors, surface the API messages directly
                        userMessage = validationErrors
                          .flatMap(error => Object.values(error))
                          .filter(Boolean)
                          .join('\n') || userMessage;
                      }
                    }

                    const errorObj = {
                      status: 400,
                      message: userMessage,
                      validation_errors: validationErrors,
                      problematic_line: errorJson.problematic_line,
                      data: errorJson,
                      originalResponse: data
                    };
                    failure(errorObj);
                  } else {
                    // Handle 400 errors without validation_errors - use backend message or full error
                    const fullMessage = errorJson.message || errorJson.error || errorText || userMessage;
                    failure(fullMessage);
                  }
                } else if (data.status === 404) {
                  // Handle 404 errors with user-friendly messages
                  let userMessage = 'The requested song or data was not found. It may have been deleted or you may not have access to it.';
                  failure(userMessage);
                } else {
                  // For other errors, use the original string format
                  let errorMessage = `Failed to fetch data with code: ${data.status}`;
                  errorMessage += ` - ${errorJson.message || errorJson.error || errorText}`;
                  failure(errorMessage);
                }
              } catch (e) {
                // If JSON parsing fails, use string format
                let errorMessage = `Failed to fetch data with code: ${data.status} - ${errorText}`;
                failure(errorMessage);
              }
            } else {
              failure('Failed to fetch data with code: ' + data.status);
            }
          } catch (e) {
            failure('Failed to fetch data with code: ' + data.status);
          }
        }
      })
      .catch(e => {
        if (!(e instanceof DOMException)) {
          failure(e.message)
        }
      })
    return newController
  }

  async function simpleFetchData(url, method, headers, body, credentials) {
    try {
      const res = await fetch(url, {
        method: method,
        headers: headers,
        body: body,
        credentials: credentials,
      })
      if (!res?.ok) {
        // Surface the server's JSON error body when present (statusText is blank
        // on many responses). Additive: status/type are unchanged; message just
        // prefers a real server message over the bare statusText.
        let serverMessage = res?.statusText
        let serverCode
        try {
          const rawBody = await res.text()
          if (rawBody) {
            try {
              const parsed = JSON.parse(rawBody)
              serverMessage =
                parsed.msg ||
                parsed.error_description ||
                parsed.message ||
                parsed.error ||
                parsed.detail ||
                res?.statusText
              // Lets a page tell apart error cases that share one HTTP status.
              serverCode = parsed.code
            } catch (parseError) {
              // Non-JSON body — fall back to the raw text if it says something.
              serverMessage = rawBody || res?.statusText
            }
          }
        } catch (readError) {
          // Body already consumed / unreadable — keep statusText.
        }
        throw {
          status: res?.status,
          message: serverMessage,
          type: res?.type,
          code: serverCode,
        }
      } else if (res.status === 401) {
        logout()
      } else if (res) {
        const contentType = res.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          const response = await res.json()
          return response
        } else {
          // Handle cases where response is not JSON
          const responseText = await res.text()
          return responseText
        }
      }
    } catch (e) {
      throw e
    }
  }

  function cancelRequest(controller) {
    if (controller !== undefined && controller !== null) {
      controller.abort()
    }
  }

  function getSongData(songFID) {
    const url = baseURL + '/music/song/' + songFID
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function fetchAllSongs(pageNumber, pageLimit, success, failure) {
    const url = baseURL + '/music/song?n=' + pageLimit + '&p=' + pageNumber
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  function getAllSongs(pageNumber, pageLimit, sort, syncedFilter) {
    let url = baseURL + '/music/song?n=' + pageLimit + '&p=' + pageNumber + '&s=' + (sort ?? 'alpha')
    if (syncedFilter && syncedFilter !== 'all') url += '&sv=' + syncedFilter
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function searchAllSongs(searchTerm, pageNumber, pageLimit, success, failure) {
    const url = baseURL + '/music/song/_search'
    let body = JSON.stringify({
      search: searchTerm,
      page: pageNumber,
      limit: pageLimit,
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }
  function getSearchAllSongs(searchTerm, pageNumber, pageLimit, artistId = '', isPromotional = undefined, isPromotionalHide = undefined) {
    const url = baseURL + '/music/song/_search'
    let body = JSON.stringify({
      search: searchTerm,
      page: pageNumber,
      limit: pageLimit,
      artist_f_id: artistId,
      is_promotional: isPromotional,
      is_promotional_hide: isPromotionalHide
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function getArtistSongs(artistUsername, pageNumber, pageLimit, sort, syncedFilter) {
    let url =
      baseURL +
      '/music/artist/' +
      artistUsername +
      '/song?pr=0' +
      '&n=' +
      pageLimit +
      '&p=' +
      pageNumber +
      '&s=' +
      (sort ?? 'alphabet')
    if (syncedFilter && syncedFilter !== 'all') {
      url += '&sv=' + syncedFilter
    }
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getArtistTeases(artistUsername, pageNumber, pageLimit, sort, syncedFilter) {
    let url =
      baseURL +
      '/music/artist/' +
      artistUsername +
      '/song?pr=1' +
      '&n=' +
      pageLimit +
      '&p=' +
      pageNumber +
      '&s=' +
      (sort ?? 'alphabet')
    if (syncedFilter && syncedFilter !== 'all') {
      url += '&sv=' + syncedFilter
    }
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getAlbumSongs(albumFID) {
    const url = baseURL + '/music/album/' + albumFID + '/song'
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getArtists(pageNumber, pageLimit) {
    const url = baseURL + '/music/artist?n=' + pageLimit + '&p=' + pageNumber
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getArtistData(artistName) {
    const url = baseURL + '/music/artist/' + artistName
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function searchArtists(searchTerm, pageNumber, pageLimit, searchAll = false) {
    const url = baseURL + '/music/artist/_search'
    let body = JSON.stringify({
      search: searchTerm,
      page: pageNumber,
      limit: pageLimit,
      _all: searchAll,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function getBackdropList(artistName, pageNumber, pageLimit, songFID) {
    const url =
      baseURL +
      '/music/artist/' +
      artistName +
      '/backdrop?n=' +
      pageLimit +
      '&p=' +
      pageNumber +
      '&s=' +
      songFID
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getBackdrop(f_id) {
    const url = baseURL + '/music/backdrop/media/' + f_id
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getUrlNewBackdrop(artistFID, backdropType, mediaType) {
    const url = baseURL + '/music/backdrop/media'
    const body = JSON.stringify({
      backdrop_type: backdropType,
      artist_f_id: artistFID,
      media_type: mediaType,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function deleteBackdrop(backdropFID) {
    const url = baseURL + '/music/backdrop/' + backdropFID
    return simpleFetchData(url, DELETE, prepareHeaders(), null, INCLUDE)
  }

  function uploadBackdropMedia(uploadURL, file, mediaType) {
    let headers = new Headers()
    headers.append('Content-Type', mediaType)
    return simpleFetchData(uploadURL, PUT, headers, file, OMIT)
  }

  function updateBackdrop(songFID, backdropFID) {
    const url = baseURL + '/music/song/' + songFID + '/backdrop'
    const body = JSON.stringify({
      backdrop_f_id: backdropFID,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function updateAlternativeBackdrops(songFID, backdropFIDs) {
    const url = baseURL + '/music/song/' + songFID + '/backdrop/_alt'
    const body = JSON.stringify({
      backdrop_f_ids: backdropFIDs,
      _action: "update"
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function fetchAlternativeBackdrops(songFID) {
    const url = baseURL + '/music/song/' + songFID + '/backdrop/_alt'
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  // OVERLAY FILTERS

  function getOverlayFilters(pageNumber, pageLimit, show = 'all') {
    const url =
      baseURL +
      '/feels/overlay_filter?n=' +
      pageLimit +
      '&p=' +
      pageNumber +
      '&show=' +
      show
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getOverlayFilter(filterFID) {
    const url = baseURL + '/feels/overlay_filter/' + filterFID
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function createOverlayFilter(token, name, metadata, source) {
    const url = baseURL + '/feels/overlay_filter'
    const body = JSON.stringify({
      token: token,
      name: name,
      metadata: metadata,
      source: source,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function updateOverlayFilter(filterFID, updates) {
    const url = baseURL + '/feels/overlay_filter/' + filterFID
    const body = JSON.stringify(updates)
    return simpleFetchData(url, PATCH, prepareHeaders(), body, INCLUDE)
  }

  function deleteOverlayFilter(filterFID) {
    const url = baseURL + '/feels/overlay_filter/' + filterFID
    return simpleFetchData(url, DELETE, prepareHeaders(), null, INCLUDE)
  }

  function publishOverlayFilter(filterFID, isPublished) {
    const url = baseURL + '/feels/overlay_filter/' + filterFID + '/publish'
    const body = JSON.stringify({ is_published: isPublished })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function getUrlNewOverlayFilterMedia() {
    const url = baseURL + '/feels/overlay_filter/media'
    return simpleFetchData(url, POST, prepareHeaders(), '{}', INCLUDE)
  }

  function uploadOverlayFilterMedia(uploadURL, file) {
    let headers = new Headers()
    headers.append('Content-Type', 'image/webp')
    return simpleFetchData(uploadURL, PUT, headers, file, OMIT)
  }

  function finalizeOverlayFilterMedia(uploadID) {
    const url = baseURL + '/feels/overlay_filter/media/_finalize'
    const body = JSON.stringify({ upload_id: uploadID })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function fetchLyricsToEdit(songFID, success, failure) {
    const url = baseURL + '/music/song/' + songFID + '/lyrics?t=admin'
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  function fetchLyricsToCreateFeel(songFID, success, failure) {
    const url = baseURL + '/music/song/' + songFID + '/lyrics?t=feels'
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  function getAlbums(artistName, pageNumber, pageLimit, showAll = false) {
    const url =
      baseURL +
      '/music/artist/' +
      artistName +
      '/album?n=' +
      pageLimit +
      '&p=' +
      pageNumber +
      '&_all=' +
      showAll
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function saveAlbumHideState(albumFID, isHidden) {
    let url = baseURL + '/music/album/' + albumFID
    const body = JSON.stringify({
      is_public_visible: isHidden,
      is_published: isHidden,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function saveSongEdits(
    songFID,
    videoOffset,
    lyrics,
    isSyncVerified,
    isLocked,
    editorEmail,
    success,
    failure
  ) {
    let url = baseURL + '/music/song/' + songFID + '/sync'
    const body = JSON.stringify({
      video_offset: videoOffset,
      lyrics_sync: lyrics,
      is_sync_verified: isSyncVerified,
      sync_user_id: editorEmail,
      feels_locked: isLocked,
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }

  function saveSongLyricsEdits(
    songFID,
    lyrics,
    editorEmail,
    success,
    failure,
    editType = 'compose'
  ) {
    let url = baseURL + '/music/song/' + songFID + '/lyrics/edit'
    const body = JSON.stringify({
      _publish: 'publish',
      edit_type: editType,
      lyrics_sync: lyrics,
      edit_user_id: editorEmail,
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }

  function runFeelsSync(songFID, success, failure, options) {
    let url = baseURL + '/music/song/' + songFID + '/f_sync'
    // options.retimer: feelssync-v2 tier override; omitted = router
    // default (fast-time).
    const body = options && options.retimer ? { retimer: options.retimer } : {}
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      JSON.stringify(body),
      INCLUDE,
      success,
      failure
    )
  }

  // QuickTime: synchronous MMS-only retime. Blocks until the timing is saved
  // (warm ~2s, cold worker up to ~3 min), then returns the freshly-timed song.
  function runQuickTime(songFID, success, failure) {
    let url = baseURL + '/music/song/' + songFID + '/quick_time'
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }


  // FeelsSync v2 job queue (mmuf_feelssync_jobs) for the /feelsyncjobs tool.
  // status filter: pending | leased | completed | failed (omit/'' = all).
  function getFeelsSyncJobs(pageNumber, pageLimit, status) {
    let url = baseURL + '/feelssync/jobs?n=' + pageLimit + '&p=' + pageNumber
    if (status && status !== 'all') url += '&status=' + status
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getIngestAccess() {
    return simpleFetchData(baseURL + '/ingest/access', GET, prepareHeaders(), null, INCLUDE)
  }

  function getIngestRequests() {
    return simpleFetchData(baseURL + '/ingest/requests', GET, prepareHeaders(), null, INCLUDE)
  }

  function getIngestRequest(id) {
    return simpleFetchData(baseURL + '/ingest/requests/' + encodeURIComponent(id), GET, prepareHeaders(), null, INCLUDE)
  }

  function startIngestRequest(input) {
    return simpleFetchData(baseURL + '/ingest/requests', POST, prepareHeaders(), JSON.stringify(input), INCLUDE)
  }

  function tendIngestRequest(id, action, decisions) {
    return simpleFetchData(baseURL + '/ingest/requests/' + encodeURIComponent(id) + '/actions', POST, prepareHeaders(), JSON.stringify({ action, decisions }), INCLUDE)
  }

  // GET /admin/user: { admin_users: [{ f_id, name, email, ... }] }. Lets the
  // Ingest pages show a requester's name instead of their raw admin f_id -
  // see IngestStatus/useAdminUserNames. Requires PERMISSION_ADMIN +
  // PERMISSION_ACCESS_ALL (admin-service-api get-admin-user.js), a higher
  // bar than plain ingest access, so a 403 here is expected for most
  // catalogue-team users; callers must fall back to the raw id on failure
  // rather than surface this as a page error.
  function getAdminUsers() {
    return simpleFetchData(baseURL + '/admin/user', GET, prepareHeaders(), null, INCLUDE)
  }

  function saveSongLockState(songFID, isLocked, editorEmail) {
    let url = baseURL + '/music/song/' + songFID
    const body = JSON.stringify({
      sync_user_id: editorEmail,
      feels_locked: isLocked,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function saveSongHideState(songFID, isHidden, editorEmail) {
    let url = baseURL + '/music/song/' + songFID
    const body = JSON.stringify({
      sync_user_id: editorEmail,
      is_public_visible: isHidden,
      is_published: isHidden,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function updateSongName(songFID, songName, editorEmail) {
    let url = baseURL + '/music/song/' + songFID
    const body = JSON.stringify({
      name: songName,
      name_full: songName, // Update name_full to match name when editing
      sync_user_id: editorEmail,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function sendSongFeedback(songFID, comment, success, failure) {
    let url = baseURL + '/music/song/' + songFID + '/report'
    const body = JSON.stringify({
      comment: comment,
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }

  function updateReportTableData(
    reportID,
    status,
    department,
    priority,
    editor_notes,
    assigned
  ) {
    let url = baseURL + '/report/' + reportID
    const body = JSON.stringify({
      ...(status !== null && { status }),
      ...(department !== null && { department }),
      ...(priority !== null && { priority }),
      ...(editor_notes !== null && { editor_notes }),
      ...(assigned !== null && { assigned }),
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function uploadArtistData(
    artistName,
    fullName,
    instagramURL,
    twitterURL,
    youtubeURL,
    tiktokURL,
    discordURL,
    mechURL,
    tourURL,
    impactURL,
    ticketmaster
  ) {
    let url = baseURL + '/music/artist/' + artistName
    var socialMedia = [
      { social_f_id: 'instagram', url: instagramURL },
      { social_f_id: 'twitter', url: twitterURL },
      { social_f_id: 'youtube', url: youtubeURL },
      { social_f_id: 'tiktok', url: tiktokURL },
      { social_f_id: 'discord', url: discordURL },
    ]
    // Always include all links, even if empty, so the backend can soft-delete them
    var links = []
    links.push({ link_type_f_id: 'merchandise', url: mechURL || '' })
    links.push({ link_type_f_id: 'tour', url: tourURL || '' })
    links.push({ link_type_f_id: 'impact', url: impactURL || '' })
    links.push({ link_type_f_id: 'ticketmaster', url: ticketmaster || '' })
    
    const body = JSON.stringify({
      artist_name: artistName,
      name: fullName,
      social_media: socialMedia,
      links: links,
    })
    
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function getArtistFeels(artistUsername, pageNumber, pageLimit, sort) {
    const url =
      baseURL +
      '/music/artist/' +
      artistUsername +
      '/mm?n=' +
      pageLimit +
      '&p=' +
      pageNumber +
      '&s=' +
      (sort ?? 'alphabet')
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getArtistPinnedFeels(artistUsername) {
    const url = baseURL + '/music/artist/' + artistUsername + '/mm/pin'
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function updateArtistPinnedMM(artistUsername, feelsFIDs) {
    const url = baseURL + '/music/artist/' + artistUsername + '/mm/pin'
    const body = JSON.stringify({ feels_f_ids: feelsFIDs })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function getFeelsForSong(songFID, pageNumber, pageLimit) {
    const url =
      baseURL +
      '/music/song/' +
      songFID +
      '/mm?n=' +
      pageLimit +
      '&p=' +
      pageNumber
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getFeelData(feelFID) {
    const url = baseURL + '/feels/mm/' + feelFID
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getFeelTags({
    feelFID,
    isUserTags = false,
    isSuggestedTags = false,
  }) {
    const url = `${baseURL}/feels/mm/${feelFID}/tags${
      isUserTags ? '/users' : isSuggestedTags ? '/suggested' : ''
    }`
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }
  function generateFeelTags({ song_f_id, lyrics_lines, generate = true }) {
    const url = `${baseURL}/feels/tags/suggested`
    const body = JSON.stringify({
      song_f_id,
      lyrics_lines,
      generate,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function requestExportFeel(feelFID) {
    const url = baseURL + '/feels/mm/' + feelFID + '/export'
    const body = JSON.stringify({ _export: true })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function exportFeel(feelFID) {
    const url = baseURL + '/feels/mm/' + feelFID + '/export'
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getFeelsCount(status) {
    const url = baseURL + '/feels/mm?ff=' + status + '&c=1'
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function fetchFeelsByStatus(status, pageNumber, pageLimit, success, failure) {
    const url =
      baseURL +
      '/feels/mm?ff=' +
      status +
      '&n=' +
      pageLimit +
      '&p=' +
      pageNumber
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  function getFeels(status, pageNumber, pageLimit, sort_order = 'alpha') {
    const url =
      baseURL +
      '/feels/mm?ff=' +
      status +
      '&n=' +
      pageLimit +
      '&p=' +
      pageNumber +
      '&s=' +
      sort_order
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function searchFeels(
    search,
    pageNumber,
    pageLimit,
    statusPage,
    artistId = '',
    isDB = false
  ) {
    const url = `${baseURL}/feels/mm/_search${isDB ? '?s=database' : ''}`
    const requestBody = {
      search: search,
      page: pageNumber,
      limit: pageLimit,
      ...(statusPage && { status: [statusPage] }),
      artist_f_id: artistId,
    }
    const body = JSON.stringify(requestBody)
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function deleteFeel(feelFID) {
    const url = baseURL + '/feels/mm/' + feelFID
    return simpleFetchData(url, DELETE, prepareHeaders(), null, INCLUDE)
  }

  function createFeel(songFID, lyricsLines, tags, useQuickCreate, success, failure) {
    const url = baseURL + '/feels/mm/'
    const body = JSON.stringify({
      song_f_id: songFID,
      lyrics_lines: lyricsLines,
      tags: tags,
      _quick: useQuickCreate
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }

  function createFeelPreview(songFID, lyricsLines, useQuickCreate, success, failure) {
    const url = baseURL + '/feels/preview'
    const body = JSON.stringify({
      song_f_id: songFID,
      lyrics_lines: lyricsLines,
      _quick: useQuickCreate
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }

  function fetchFeelPreview(feelFID, success, failure) {
    const url = baseURL + '/feels/preview/' + feelFID
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  function publishFeel(feelFID) {
    const url = baseURL + '/feels/mm/' + feelFID + '/publish'
    const body = JSON.stringify({ _publish: true })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function unpublishFeel(feelFID) {
    const url = baseURL + '/feels/mm/' + feelFID + '/status'
    const body = JSON.stringify({ status: 'pending_review' })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function updateTagsOnFeel(feelFID, tags) {
    const url = baseURL + '/feels/mm/' + feelFID + '/tags'
    const body = JSON.stringify({ _action: 'update', tags: tags })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function refreshFeel(feelFID, songFID, lyricsLines) {
    // POST /feels/mm is keyed on (song_f_id, lyrics_lines) — see feels_mm.js
    // POST handler's getFeels lookup. The endpoint's schema (feels_vars) does
    // not accept feel_f_id, so sending it would be a no-op. feelFID is kept
    // in the signature for caller-side traceability and future callers that
    // may target the action endpoint differently.
    const url = baseURL + '/feels/mm'
    const body = JSON.stringify({
      song_f_id: songFID,
      lyrics_lines: lyricsLines,
      _rerender: true,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function reportSong(songFID, reportText, reporterEmail, success, failure) {
    const url = baseURL + '/music/song/' + songFID + '/report'
    const body = JSON.stringify({
      report_text: reportText,
      report_user_id: reporterEmail,
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }

  function submitReport(reportData) {
    const url = baseURL + '/report'
    return simpleFetchData(
      url,
      POST,
      prepareHeaders(),
      JSON.stringify(reportData),
      INCLUDE
    )
  }

  function getArtistImageUrl(artistName, fileType, fileSize) {
    const url = baseURL + '/music/artist/' + artistName + '/image'
    const body = JSON.stringify({
      image_type: fileType,
      file_size: fileSize,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function uploadArtistImage(uploadURL, file, fileType) {
    let headers = new Headers()
    headers.append('Content-Type', fileType)
    return simpleFetchData(uploadURL, PUT, headers, file, OMIT)
  }

  function deleteArtist(artistName) {
    const url = baseURL + '/music/artist/' + artistName
    return simpleFetchData(url, DELETE, prepareHeaders(), null, INCLUDE)
  }

  function fetchGuestToken(success, failure) {
    const url = baseURL + '/service/superset/embed_guest_token'
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  // COLLECTION GROUPS (Editorial Collections)

  async function getCollectionGroupList(groupFId) {
    // Fetches all collections in a group with pagination (returns full collection data)
    const allCollections = []
    let pageNum = 1
    const pageLimit = 50

    while (true) {
      const url = baseURL + '/collection/group/' + groupFId + '/list?p=' + pageNum + '&n=' + pageLimit
      const response = await simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)

      const lists = response.lists || []
      allCollections.push(...lists)

      const totalCount = response.lists_count || 0
      if (allCollections.length >= totalCount || lists.length === 0) {
        break
      }
      pageNum++
    }

    return { collections: allCollections, total_count: allCollections.length }
  }

  async function updateCollectionGroupList(groupFId, listFIds) {
    const url = baseURL + '/collection/group/' + groupFId + '/list'
    const body = JSON.stringify({
      _action: 'update',
      list_f_ids: listFIds
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  // COLLECTIONS

  function getCollections(pageNumber, pageLimit) {
    const url = baseURL + '/collection/list?o=mm&n=' + pageLimit + '&p=' + pageNumber
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  // Create tab "YouTube Music Trending" list (the Create Trending tab on
  // /featured-collection-update): app -> its list -> hydrated songs in
  // Redis order, by token.
  function getCreateTrendingSongs(token) {
    const url = baseURL + '/collection/app/token/' + encodeURIComponent(token) + '/songs'
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getCollection(collectionFID, pageNumber, pageLimit) {
    const url =
      baseURL +
      '/collection/list/' +
      collectionFID +
      '/object?n=' +
      pageLimit +
      '&p=' +
      pageNumber
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getCollectionById(collectionFID) {
    const url = baseURL + '/collection/list/' + collectionFID
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function updateCollection(collectionFID, name, isPublic, feelsFID, header) {
    const url = baseURL + '/collection/list/' + collectionFID
    const bodyObj = {
      name: name,
      global_type: 'featured',
      collection_object: 'mm',
      is_public: isPublic,
      object_f_ids: feelsFID,
    }
    if (header) {
      bodyObj.header = header
    }
    const body = JSON.stringify(bodyObj)
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function addCollection(name, isPublic, feelsFID) {
    const url = baseURL + '/collection/list'
    const body = JSON.stringify({
      name: name,
      global_type: 'curated',
      collection_object: 'mm',
      is_public: isPublic,
      object_f_ids: feelsFID,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function publishToggleCollection(collectionFID, isPublic) {
    const url = baseURL + '/collection/list/' + collectionFID
    const body = JSON.stringify({ _action: 'update', is_public: isPublic })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function updateCollectionPartial(collectionFID, fields) {
    const url = baseURL + '/collection/list/' + collectionFID
    const body = JSON.stringify({ _action: 'update', ...fields })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function deleteCollectionFeel(feelFID, songFID) {
    const url = baseURL + '/collection/list/' + feelFID + '/object'
    const body = JSON.stringify({ _action: 'delete', object_f_ids: [songFID] })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function addFeelsToCollection(collectionFID, feelFIDs) {
    const url = baseURL + '/collection/list/' + collectionFID + '/object'
    const body = JSON.stringify({ _action: 'add', object_f_ids: feelFIDs })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function deleteCollection(collectionFID) {
    const url = baseURL + '/collection/list/' + collectionFID
    return simpleFetchData(url, DELETE, prepareHeaders(), null, INCLUDE)
  }

  function getCollectionImageUrl(collectionFID, type, size) {
    const url = baseURL + '/collection/list/' + collectionFID + '/image'
    const body = JSON.stringify({
      image_type: type,
      file_size: size,
      preserve_aspect_ratio: true,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function uploadCollectionImage(uploadURL, file, fileType) {
    let headers = new Headers()
    headers.append('Content-Type', fileType)
    // const body = JSON.stringify({ file, preserve_aspect_ratio: true })
    return simpleFetchData(uploadURL, PUT, headers, file, OMIT)
  }

  function getCollectionHeaderImageUrl(collectionFID, imageType, fileSize, headerMedia, dimensions) {
    const url = baseURL + '/collection/list/' + collectionFID + '/header/image'
    const bodyObj = {
      image_type: imageType,
      header_media: headerMedia,
      file_size: fileSize,
    }
    if (dimensions) {
      bodyObj.dimensions = dimensions
    }
    const body = JSON.stringify(bodyObj)
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  // ORGANIZATIONS

  // function fecthOrgAccount(success, failure) {
  //   const url = baseURL + '/organization/account'
  //   return fetchData(
  //     url,
  //     GET,
  //     prepareHeaders(),
  //     null,
  //     INCLUDE,
  //     success,
  //     failure
  //   )
  // }

  // function fetchOrgUserList(orgFId, success, failure) {
  //   const url = baseURL + '/organization/' + orgFId + '/user'
  //   return fetchData(
  //     url,
  //     GET,
  //     prepareHeaders(),
  //     null,
  //     INCLUDE,
  //     success,
  //     failure
  //   )
  // }

  function createNewOrgUser(orgFId, email, success, failure) {
    const url = baseURL + '/organization/' + orgFId + '/user?idp=1'
    const body = JSON.stringify({
      email: email,
      login_type: 'google',
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      data => {
        data.json().then(json => {
          if (json.f_id !== undefined && json.f_id !== null) {
            const userFId = json.f_id

            fetchGroupID(
              orgFId,
              data => {
                data.json().then(json => {
                  if (
                    json.organization_groups !== undefined &&
                    json.organization_groups !== null &&
                    json.organization_groups.length > 0 &&
                    json.organization_groups[0].f_id !== undefined &&
                    json.organization_groups[0].f_id !== null
                  ) {
                    const groupFId = json.organization_groups[0].f_id

                    changeOrgRole(
                      orgFId,
                      groupFId,
                      'viewer',
                      'add',
                      userFId,
                      success,
                      () => {
                        // In either case this is a failure to the higher callback
                        deleteOrgUser(orgFId, userFId, failure, failure)
                      }
                    )
                  } else {
                    // In either case this is a failure to the higher callback
                    deleteOrgUser(orgFId, userFId, failure, failure)
                  }
                })
              },
              failure
            )
          } else {
            // In either case this is a failure to the higher callback
            deleteOrgUser(orgFId, userFId, failure, failure)
          }
        })
      },
      failure
    )
  }

  function changeOrgRole(
    orgFId,
    groupFId,
    role,
    action,
    userFId,
    success,
    failure
  ) {
    const url =
      baseURL +
      '/organization/' +
      orgFId +
      '/group/' +
      groupFId +
      '/role/' +
      role +
      '/user'
    const body = JSON.stringify({
      _action: action,
      organization_users: [userFId],
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }

  function deleteOrgUser(orgFId, userFId, success, failure) {
    const url = baseURL + '/organization/' + orgFId + '/user/' + userFId
    return fetchData(
      url,
      DELETE,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  function fetchGroupID(orgFId, success, failure) {
    const url = baseURL + '/organization/' + orgFId + '/group?n=Organization'
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  // ADMIN

  // function fecthAdminAccount(success, failure) {
  //   const url = baseURL + '/admin/account'
  //   return fetchData(
  //     url,
  //     GET,
  //     prepareHeaders(),
  //     null,
  //     INCLUDE,
  //     success,
  //     failure
  //   )
  // }

  // function fetchAdminUserList(success, failure) {
  //   const url = baseURL + '/admin/user'
  //   return fetchData(
  //     url,
  //     GET,
  //     prepareHeaders(),
  //     null,
  //     INCLUDE,
  //     success,
  //     failure
  //   )
  // }

  function createNewAdminUser(email, success, failure) {
    const url = baseURL + '/admin/user'
    const body = JSON.stringify({
      email: email,
      login_type: 'google',
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      data => {
        data.json().then(json => {
          if (json.f_id !== undefined && json.f_id !== null) {
            const userFId = json.f_id

            changeAdminRole('viewer', 'add', userFId, success, () => {
              // In either case this is a failure to the higher callback
              deleteAdminUser(userFId, failure, failure)
            })
          } else {
            // In either case this is a failure to the higher callback
            deleteAdminUser(userFId, failure, failure)
          }
        })
      },
      failure
    )
  }

  function changeAdminRole(role, action, userFId, success, failure) {
    const url = baseURL + '/admin/role/' + role + '/user'
    const body = JSON.stringify({
      _action: action,
      admin_users: [userFId],
    })
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      success,
      failure
    )
  }

  function deleteAdminUser(userFId, success, failure) {
    const url = baseURL + '/admin/user/' + userFId
    return fetchData(
      url,
      DELETE,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  function fetchCurrentUser(success, failure) {
    const url = baseURL + '/account'
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  function sendResetPassword(email) {
    const url = baseURL + '/organization/idp/_password'
    return simpleFetchData(
      url,
      POST,
      prepareHeaders(),
      JSON.stringify({ email }),
      INCLUDE
    )
  }

  function resendOrganizationInvite({ email }) {
    const url = `${baseURL}/organization/idp/_invite`
    return simpleFetchData(
      url,
      POST,
      prepareHeaders(),
      JSON.stringify({ email }),
      INCLUDE
    )
  }

  // Tease

  async function getData(url, method, headers, body, credentials) {
    try {
      const newController = new AbortController()
      const signal = newController.signal
      const response = await fetch(url, {
        method,
        headers,
        body,
        signal,
        credentials,
      })
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      const data = await response.json()
      return data
    } catch (error) {
      throw error
    }
  }

  async function createTease({
    is_promotional = true,
    songFID = null,
    ...rest
  }) {
    const song_id = songFID ? `/${songFID}` : ''
    const url = baseURL + '/music/song' + song_id
    const body = JSON.stringify({
      is_promotional,
      ...rest,
    })
    return getData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  async function createTeaseVideo({ payload, tease_id }) {
    const url = baseURL + `/music/song/${tease_id}/video`
    const body = JSON.stringify(payload)
    return getData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  async function uploadTeaseVideo({ uploadURL, file }) {
    let headers = new Headers()
    headers.append('Content-Type', file.type)
    return simpleFetchData(uploadURL, PUT, headers, file, OMIT)
  }

  async function editTeaseLyrics({ payload, tease_id }) {
    const url = baseURL + `/music/song/${tease_id}/lyrics/edit`
    const body = JSON.stringify({
      _publish: 'publish',
      edit_type: 'compose',
      ...payload,
    })
    return getData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  async function fetchTease(tease_id) {
    const url = `${baseURL}/music/song/${tease_id}/lyrics?t=admin`
    return getData(url, 'GET', prepareHeaders(), null, INCLUDE)
  }

  async function saveTeaseEdits({ songFID, ...rest }) {
    let url = baseURL + '/music/song/' + songFID + '/sync'
    const body = JSON.stringify({
      ...rest,
    })
    return getData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  // Organizations

  function getOrganizationsList({ p, n }) {
    const url = baseURL + '/organization?p=' + p + '&n=' + n
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }
  function getOrganizationsAccount() {
    const url = `${baseURL}/organization/account`
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getOrganizationDetails({ id }) {
    const url = `${baseURL}/organization/${id}`
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getTeamDetails({ o_f_id, g_f_id }) {
    const url = `${baseURL}/organization/${o_f_id}/group/${g_f_id}`
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getOrganizationGroups({ o_f_id, n }) {
    const url = `${baseURL}/organization/${o_f_id}/group${n ? '/?n=' + n : ''}`
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getOrganizationUsers({ o_f_id, u_f_id }) {
    const url = `${baseURL}/organization/${o_f_id}/user${
      u_f_id ? '/' + u_f_id : ''
    }`
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getOrganizationGroupUsers({ o_f_id, role, g_f_id }) {
    const url = `${baseURL}/organization/${o_f_id}/group/${g_f_id}/role/${role}/user`

    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function createOrganization({ name, o_f_id = null, ...rest }) {
    const url = `${baseURL}/organization${o_f_id ? '/' + o_f_id : ''}`
    const body = JSON.stringify({
      name,
      ...rest,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function createOrganizationGroup({ name, o_f_id, g_f_id, ...rest }) {
    const url = `${baseURL}/organization/${o_f_id}/group${
      g_f_id ? '/' + g_f_id : ''
    }`
    const body = JSON.stringify({
      name,
      ...rest,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function createOrganizationUser({ o_f_id, ...rest }) {
    const url = `${baseURL}/organization/${o_f_id}/user?idp=1`
    const body = JSON.stringify({
      login_type: 'google',
      ...rest,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }
  function deleteOrganizationUser({ o_f_id, u_f_id }) {
    const url = `${baseURL}/organization/${o_f_id}/user/${u_f_id}`

    return simpleFetchData(url, DELETE, prepareHeaders(), null, INCLUDE)
  }

  function getOrganizationUser({ o_f_id, u_f_id }) {
    const url = `${baseURL}/organization/${o_f_id}/user/${u_f_id}`

    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function changeOrganizationUsersRole({ o_f_id, g_f_id, role, ...rest }) {
    const url = `${baseURL}/organization/${o_f_id}/group/${g_f_id}/role/${role}/user`
    const body = JSON.stringify({
      ...rest,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function manageOrganizationContent({ o_f_id, content_object, ...rest }) {
    const url = `${baseURL}/organization/${o_f_id}/content/${content_object}`
    const body = JSON.stringify({
      ...rest,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function deleteOrganizationGroup({ o_f_id, g_f_id }) {
    const url = `${baseURL}/organization/${o_f_id}/group/${g_f_id}`
    return fetchData(
      url,
      DELETE,
      prepareHeaders(),
      null,
      INCLUDE,
      () => {},
      () => {}
    )
  }

  function manageOrganizationGroupContent({
    o_f_id,
    g_f_id,
    content_object,
    ...rest
  }) {
    const url = `${baseURL}/organization/${o_f_id}/group/${g_f_id}/content/${content_object}`
    const body = JSON.stringify({
      ...rest,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function getOrganizationContent({
    o_f_id,
    content_object,
    pageParam,
    perPage,
  }) {
    const url = `${baseURL}/organization/${o_f_id}/content/${content_object}?n=${perPage}&p=${pageParam}`

    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function getOrganizationGroupContent({
    o_f_id,
    g_f_id,
    content_object,
    pageParam,
    perPage,
  }) {
    const url = `${baseURL}/organization/${o_f_id}/group/${g_f_id}/content/${content_object}?n=${perPage}&p=${pageParam}`

    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  // function getOrganizationGroupContent({ o_f_id, g_f_id, content_object, n }) {
  //   const url = `${baseURL}/organization/${o_f_id}/group/${g_f_id}/content/${content_object}`
  //   return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  // }

  function getAuthProvidersOfEmail(email) {
    const url = `${baseURL}/organization/idp/_login`
    const body = JSON.stringify({
      email,
    })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function getAllReports(params) {
    const queryParams = new URLSearchParams()
    if (params?.o) queryParams.append('o', params.o)
    if (params?.s) queryParams.append('s', params.s)
    if (params?.r) queryParams.append('r', params.r)
    if (params?.p) queryParams.append('p', params.p)

    const url = `${baseURL}/report?${queryParams.toString()}&n=20`
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  // INVITE CODES
  function fetchAllInviteCodes(pageNumber, pageLimit, success, failure, includeInactive = true) {
    // Try without the version prefix first
    let url = baseURL + '/app/invite?p=' + pageNumber + '&n=' + pageLimit
    // Add parameters to include expired codes
    if (includeInactive) {
      url += '&x=true' // Show expired invites
    }
    return fetchData(
      url,
      GET,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  // Alternative function using simpleFetchData for invite codes
  function getAllInviteCodes(pageNumber, pageLimit, includeInactive = true, includeUserFeelsSend = false) {
    let url = baseURL + '/app/invite?p=' + pageNumber + '&n=' + pageLimit
    // Add parameters to include expired codes
    if (includeInactive) {
      url += '&x=true' // Show expired invites
    }
    // Add rgox= parameter to include user_feels_send codes
    if (includeUserFeelsSend) {
      url += '&rgox='
    }
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }


  // Try alternative endpoints for invite codes
  function tryAlternativeInviteEndpoints(pageNumber, pageLimit, includeInactive = true) {
    const statusParams = includeInactive ? ['&x=true', '&x=true&exclude=user_feels_send', '&include_deleted=true&include_expired=true', '&status=all', ''] : ['']
    
    const baseEndpoints = [
      '/app/invite',  // Try non-versioned first
      '/1.0/app/invite',
      '/invite',
      '/admin/invite',
      '/app/invites',  // Try plural form
      '/admin/invites',
      '/app/codes',
      '/admin/codes',
      '/app/invite/list',
      '/admin/invite/list'
    ]
    
    // Generate all combinations of endpoints with status parameters
    const endpoints = []
    baseEndpoints.forEach(base => {
      statusParams.forEach(status => {
        endpoints.push(base + '?p=' + pageNumber + '&n=' + pageLimit + status)
      })
    })
    
    const promises = endpoints.map(endpoint => {
      const url = baseURL + endpoint
      return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
        .then(data => ({ endpoint, data, success: true }))
        .catch(error => ({ endpoint, error, success: false }))
    })
    
    return Promise.allSettled(promises)
  }

  function createInviteCode(inviteLimit, expirationDate, success, failure, customInviteCode = null, allowMultipleSignup = true, referralGroupObject = null, feels = null, geoRestricted = false) {
    const url = baseURL + '/app/invite'
    
    // Convert date format if needed - API expects ISO format
    let formattedDate = expirationDate;
    if (expirationDate && expirationDate !== 'No expiration') {
      try {
        const date = new Date(expirationDate);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toISOString();
        }
      } catch (e) {
        console.warn('Date conversion failed:', e);
      }
    }
    
    const requestBody = {
      max_signup_count: inviteLimit,
      date_expire: formattedDate === 'No expiration' ? null : formattedDate,
      allow_multiple_signup: allowMultipleSignup,
      invite_token: customInviteCode && customInviteCode.trim() ? customInviteCode.trim() : generateRandomToken(),
      geo_restricted: geoRestricted
    };
    
    // Add referral group object if provided
    if (referralGroupObject) {
      requestBody.referral_group_object = referralGroupObject;
    }
    
    // Add feels object if provided
    if (feels) {
      requestBody.feels = feels;
    }
    
    if (customInviteCode && customInviteCode.trim()) {
      // Using custom invite token
    } else {
      // Generated random invite token
    }
    
    // Helper function to generate random token
    function generateRandomToken() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
      let result = '';
      for (let i = 0; i < 6; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      return result;
    }
    
    const body = JSON.stringify(requestBody);
    
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      (response) => {
        success(response);
      },
      (error) => {
        console.error('❌ Create invite code failed:', error);
        // Try to get more detailed error information
        if (error && typeof error === 'object') {
          console.error('Detailed error:', JSON.stringify(error, null, 2));
        }
        failure(error);
      }
    )
  }

  function updateInviteCode(token, inviteLimit, expirationDate, isDeleted = false, isExpired = null, success, failure, geoRestricted = null) {
    const url = baseURL + '/app/invite/' + token
    
    // Convert date format if needed - API expects ISO format
    let formattedDate = expirationDate;
    if (expirationDate && expirationDate !== 'No expiration') {
      // Try to parse and convert to ISO format
      try {
        const date = new Date(expirationDate);
        if (!isNaN(date.getTime())) {
          formattedDate = date.toISOString();
        }
      } catch (e) {
        console.warn('Date conversion failed:', e);
      }
    }
    
    const requestBody = {
      max_signup_count: inviteLimit,
      date_expire: formattedDate === 'No expiration' ? null : formattedDate,
      is_deleted: isDeleted,
      allow_multiple_signup: true // Always allow multiple signups (active state is calculated server-side)
    };
    
    
    // Add is_expired field only if explicitly set
    if (isExpired !== null) {
      requestBody.is_expired = isExpired;
    }
    
    // Add geo_restricted field only if explicitly set
    if (geoRestricted !== null) {
      requestBody.geo_restricted = geoRestricted;
    }
    
    const body = JSON.stringify(requestBody);
    
    return fetchData(
      url,
      POST,
      prepareHeaders(),
      body,
      INCLUDE,
      (response) => {
        if (success) success(response);
      },
      failure
    )
  }

  function deleteInviteCode(token, success, failure) {
    const url = baseURL + '/app/invite/' + token
    return fetchData(
      url,
      DELETE,
      prepareHeaders(),
      null,
      INCLUDE,
      success,
      failure
    )
  }

  // ISRC EXCEPTIONS
  // Memoized so consumers can safely list them in useEffect deps without
  // triggering an infinite re-fetch loop (per Gemini review on PR #278).
  const getIsrcExceptions = useCallback(
    ({ limit = 100, offset = 0, search = '' } = {}) => {
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      })
      if (search) params.set('search', search)
      const url = `${baseURL}/isrc_exceptions?${params.toString()}`
      return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
    },
    [baseURL]
  )

  const resolveIsrcException = useCallback(
    payload => {
      const url = `${baseURL}/isrc_exceptions/resolve`
      return simpleFetchData(url, POST, prepareHeaders(), JSON.stringify(payload), INCLUDE)
    },
    [baseURL]
  )

  // ALLOWLIST MANAGEMENT APIs
  function getAllowlistFromServer() {
    const url = baseURL + '/admin/allowlist/codes'
    return simpleFetchData(url, GET, prepareHeaders(), null, INCLUDE)
  }

  function saveAllowlistToServer(allowlist) {
    const url = baseURL + '/admin/allowlist/codes'
    const body = JSON.stringify({ allowlist: allowlist })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function addUserToServerAllowlist(username) {
    const url = baseURL + '/admin/allowlist/codes/user'
    const body = JSON.stringify({ username: username.toLowerCase() })
    return simpleFetchData(url, POST, prepareHeaders(), body, INCLUDE)
  }

  function removeUserFromServerAllowlist(username) {
    const url = baseURL + '/admin/allowlist/codes/user/' + encodeURIComponent(username.toLowerCase())
    return simpleFetchData(url, DELETE, prepareHeaders(), null, INCLUDE)
  }

  const value = {
    getAuthProvidersOfEmail,
    getSongData,
    fetchAllSongs,
    getAllSongs,
    searchAllSongs,
    getSearchAllSongs,
    getAlbumSongs,
    getArtists,
    getArtistData,
    searchArtists,
    getBackdropList,
    getUrlNewBackdrop,
    deleteBackdrop,
    uploadBackdropMedia,
    updateBackdrop,
    updateAlternativeBackdrops,
    fetchAlternativeBackdrops,
    getOverlayFilters,
    getOverlayFilter,
    createOverlayFilter,
    updateOverlayFilter,
    deleteOverlayFilter,
    publishOverlayFilter,
    getUrlNewOverlayFilterMedia,
    uploadOverlayFilterMedia,
    finalizeOverlayFilterMedia,
    fetchLyricsToEdit,
    fetchLyricsToCreateFeel,
    getAlbums,
    saveAlbumHideState,
    saveSongEdits,
    saveSongLyricsEdits,
    runFeelsSync,
    runQuickTime,
    getFeelsSyncJobs,
    getIngestAccess,
    getIngestRequests,
    getIngestRequest,
    startIngestRequest,
    tendIngestRequest,
    getAdminUsers,
    saveSongLockState,
    saveSongHideState,
    updateSongName,
    getArtistSongs,
    sendSongFeedback,
    updateReportTableData,
    uploadArtistData,
    getFeelsForSong,
    getFeelData,
    requestExportFeel,
    exportFeel,
    getFeelsCount,
    fetchFeelsByStatus,
    deleteFeel,
    createFeel,
    createFeelPreview,
    fetchFeelPreview,
    publishFeel,
    unpublishFeel,
    updateTagsOnFeel,
    refreshFeel,
    reportSong,
    submitReport,
    getArtistImageUrl,
    uploadArtistImage,
    deleteArtist,
    fetchGuestToken,
    createNewOrgUser,
    changeOrgRole,
    deleteOrgUser,
    fetchGroupID,
    createNewAdminUser,
    changeAdminRole,
    deleteAdminUser,
    fetchCurrentUser,
    sendResetPassword,
    cancelRequest,
    getCollectionGroupList,
    updateCollectionGroupList,
    getCollections,
    getCollection,
    getCreateTrendingSongs,
    getCollectionById,
    deleteCollectionFeel,
    addFeelsToCollection,
    deleteCollection,
    publishToggleCollection,
    updateCollectionPartial,
    addCollection,
    getFeels,
    searchFeels,
    updateCollection,
    getCollectionImageUrl,
    uploadCollectionImage,
    getCollectionHeaderImageUrl,
    getFeelTags,
    getArtistFeels,
    getArtistPinnedFeels,
    updateArtistPinnedMM,
    createTease,
    createTeaseVideo,
    uploadTeaseVideo,
    editTeaseLyrics,
    fetchTease,
    saveTeaseEdits,
    getArtistTeases,
    generateFeelTags,
    getOrganizationsList,
    createOrganization,
    manageOrganizationContent,
    getOrganizationGroups,
    createOrganizationUser,
    changeOrganizationUsersRole,
    getOrganizationDetails,
    getOrganizationUsers,
    getOrganizationUser,
    getOrganizationContent,
    deleteOrganizationUser,
    createOrganizationGroup,
    getOrganizationGroupContent,
    manageOrganizationGroupContent,
    getTeamDetails,
    getOrganizationGroupUsers,
    getOrganizationsAccount,
    getBackdrop,
    deleteOrganizationGroup,
    getAllReports,
    fetchAllInviteCodes,
    getAllInviteCodes,
    tryAlternativeInviteEndpoints,
    createInviteCode,
    updateInviteCode,
    deleteInviteCode,
    getAllowlistFromServer,
    saveAllowlistToServer,
    addUserToServerAllowlist,
    removeUserFromServerAllowlist,
    resendOrganizationInvite,
    getIsrcExceptions,
    resolveIsrcException,
  }
  return <APIContext.Provider value={value}>{children}</APIContext.Provider>
}

APIProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
